import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { EditorActions } from '@atlaskit/editor-core';

import { filterToMarkdownSafeAdf } from './filterToMarkdownSafeAdf.ts';
import { isMarkdownConversionLossy } from './isMarkdownConversionLossy.ts';
import { useIsMarkdownMode } from './useIsMarkdownMode.ts';
import { useUpdateMarkdownMode } from './useUpdateMarkdownMode.ts';

const i18n = defineMessages({
	convertToMarkdownError: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-markdown-error',
		description: 'Error flag shown when converting a live page to markdown mode fails',
		defaultMessage: 'Failed to convert to markdown',
	},
	convertToRichTextError: {
		id: 'content-markdown-mode.use-markdown-mode-actions.convert-to-rich-text-error',
		description:
			'Error flag shown when converting a live page from markdown mode back to rich text fails',
		defaultMessage: 'Failed to convert to rich text',
	},
});

/**
 * Mirrors `EditorObjectHeaderContext.editorActions`: either the real
 * `EditorActions` class (when the editor is mounted) or a minimal
 * placeholder with just `isDocumentEmpty` (when it isn't).
 */
type DefaultNoop = () => void;
type EmptyEditorActions = {
	isDocumentEmpty: DefaultNoop;
};
export type EditorActionsOrEmpty = EditorActions | EmptyEditorActions;

/**
 * Structural shape we need from `FlagsStateContainer`. Declared locally so
 * this package doesn't need to depend on `@confluence/flags`.
 */
export type FlagsLike = {
	showErrorFlag: (descriptor: { title: string }) => Promise<unknown> | unknown;
};

type UseMarkdownModeActionsArgs = {
	contentId: string;
	isLivePage: boolean;
	editorActions: EditorActionsOrEmpty;
	flags: FlagsLike;
};

export type UseMarkdownModeActionsResult = {
	isMarkdownMode: boolean;
	isUpdating: boolean;
	isLossyDialogOpen: boolean;
	closeLossyDialog: () => void;
	convertToMarkdown: () => Promise<void>;
	convertToRichText: () => Promise<void>;
	confirmLossyConvertToMarkdown: () => Promise<{ success: boolean }>;
};

/**
 * Encapsulates the markdown-mode toggle flow for a live page:
 *  - reads current mode + mutation loading state,
 *  - drains in-flight collab steps before flipping the BE flag (avoids
 *    NCS-restore-replay duplication, see `drainUnconfirmedSteps`),
 *  - opens a confirmation dialog when conversion is lossy,
 *  - on dialog-confirm, rewrites the doc with the markdown-safe filter so
 *    the user immediately sees what will be persisted.
 */
export const useMarkdownModeActions = ({
	contentId,
	isLivePage,
	editorActions,
	flags,
}: UseMarkdownModeActionsArgs): UseMarkdownModeActionsResult => {
	const intl = useIntl();
	const { updateMarkdownMode, loading: isUpdating } = useUpdateMarkdownMode();
	const isMarkdownMode = useIsMarkdownMode(isLivePage ? contentId : undefined);
	const [isLossyDialogOpen, setIsLossyDialogOpen] = useState(false);

	const closeLossyDialog = useCallback(() => setIsLossyDialogOpen(false), []);

	// Wait for any unconfirmed collab steps to be acked before flipping the
	// markdown-mode flag. The convert mutation invalidates the live-doc
	// session on the BE, which triggers an NCS restore that would otherwise
	// re-apply our unconfirmed steps on top of the server doc that already
	// has them — duplicating anything typed just before the click.
	const drainUnconfirmedSteps = useCallback(async () => {
		if (!('getResolvedEditorState' in editorActions)) {
			return;
		}
		try {
			await editorActions.getResolvedEditorState('publish');
		} catch {
			// Swallow — don't block convert if the drain fails.
		}
	}, [editorActions]);

	// Flip the BE markdown-mode flag. Does NOT touch the editor doc — for
	// non-lossy ADF the existing content round-trips cleanly, and calling
	// `replaceDocument` here would re-init editor state and leave the
	// rich→md transition stuck in a loading state. The dialog-confirm path
	// (`confirmLossyConvertToMarkdown`) handles lossy rewrites separately.
	const flipFlagToMarkdown = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, true);
		if (!result.success) {
			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.convertToMarkdownError),
			});
			return { success: false };
		}
		return { success: true };
	}, [contentId, drainUnconfirmedSteps, flags, intl, updateMarkdownMode]);

	// Lossy path: after the BE flag flip, strip nodes/marks that won't
	// survive markdown round-trip and push the filtered ADF back into the
	// editor so the user sees the final state immediately.
	//
	// TODO(cc-markdown-mode): remove once the BE owns the rewrite.
	const confirmLossyConvertToMarkdown = useCallback(async () => {
		const result = await flipFlagToMarkdown();
		if (!result.success) {
			return result;
		}

		if ('replaceDocument' in editorActions && 'getValue' in editorActions) {
			try {
				const currentAdf = await editorActions.getValue();
				const filteredAdf = filterToMarkdownSafeAdf(currentAdf);
				if (filteredAdf) {
					editorActions.replaceDocument(filteredAdf);
				}
			} catch {
				// Editor may already be unmounted; the flag flip succeeded so
				// the next mount will load the rewritten doc from the BE.
			}
		}

		return { success: true };
	}, [editorActions, flipFlagToMarkdown]);

	// Entry point for the "Convert to markdown" menu item. Inspect the doc:
	// if it has rich content that won't round-trip, open the dialog;
	// otherwise just flip the flag.
	const convertToMarkdown = useCallback(async () => {
		let adf: unknown;
		if ('getValue' in editorActions) {
			try {
				adf = await editorActions.getValue();
			} catch {
				adf = undefined;
			}
		}

		if (isMarkdownConversionLossy(adf)) {
			setIsLossyDialogOpen(true);
			return;
		}

		await flipFlagToMarkdown();
	}, [editorActions, flipFlagToMarkdown]);

	// md → rich uses the same drain path; the BE-driven session restart
	// produces the same potential duplication.
	const convertToRichText = useCallback(async () => {
		await drainUnconfirmedSteps();
		const result = await updateMarkdownMode(contentId, false);
		if (!result.success) {
			void flags.showErrorFlag({
				title: intl.formatMessage(i18n.convertToRichTextError),
			});
		}
	}, [contentId, drainUnconfirmedSteps, flags, intl, updateMarkdownMode]);

	return {
		isMarkdownMode,
		isUpdating,
		isLossyDialogOpen,
		closeLossyDialog,
		convertToMarkdown,
		convertToRichText,
		confirmLossyConvertToMarkdown,
	};
};
